<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import agentStudioAPI from 'dashboard/api/agentStudio';

import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const isLoading = ref(true);
const isSyncing = ref(false);
const settings = ref({
  organization_id: null,
  configured: false,
  user_synced: false,
  users: [],
});
const error = ref(null);

const globalConfig = useMapGetter('globalConfig/get');

const agentStudioUrl = computed(() => {
  return globalConfig.value?.agentStudioUrl || null;
});

const isConnected = computed(() => {
  return settings.value.configured && settings.value.organization_id;
});

const studioDashboardUrl = computed(() => {
  if (!agentStudioUrl.value || !settings.value.organization_id) {
    return null;
  }
  return `${agentStudioUrl.value}`;
});

const syncButtonLabel = computed(() => {
  return isSyncing.value
    ? t('INTEGRATION_SETTINGS.AGENT_STUDIO.SYNC.SYNCING')
    : t('INTEGRATION_SETTINGS.AGENT_STUDIO.SYNC.BUTTON');
});

const fetchSettings = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    const response = await agentStudioAPI.getSettings();
    settings.value = response.data;
  } catch (err) {
    error.value =
      err.message || t('INTEGRATION_SETTINGS.AGENT_STUDIO.SYNC.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const handleSync = async () => {
  isSyncing.value = true;
  error.value = null;
  try {
    await agentStudioAPI.sync();
    useAlert(t('INTEGRATION_SETTINGS.AGENT_STUDIO.SYNC.SUCCESS'));
    // Refresh settings to get updated users list
    await fetchSettings();
  } catch (err) {
    error.value =
      err.response?.data?.error ||
      t('INTEGRATION_SETTINGS.AGENT_STUDIO.SYNC.ERROR');
    useAlert(error.value);
  }
  isSyncing.value = false;
};

const openStudio = () => {
  if (studioDashboardUrl.value) {
    window.open(studioDashboardUrl.value, '_blank');
  }
};

const getRoleBadgeClass = role => {
  return role === 'administrator'
    ? 'bg-n-violet-3 text-n-violet-11'
    : 'bg-n-slate-3 text-n-slate-11';
};

onMounted(() => {
  fetchSettings();
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-6xl mx-auto">
    <div v-if="isLoading" class="flex items-center justify-center flex-1 py-20">
      <Spinner size="" color-scheme="primary" />
    </div>

    <div v-else class="flex flex-col gap-6">
      <!-- Header Card -->
      <div
        class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow gap-6"
      >
        <div
          class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
        >
          <div class="flex h-16 w-16 items-center justify-center flex-shrink-0">
            <div
              class="flex items-center justify-center w-16 h-16 rounded-md bg-n-alpha-2 border border-n-weak"
            >
              <span class="i-ph-robot text-3xl text-n-slate-11" />
            </div>
          </div>
          <div>
            <h3 class="mb-1 text-xl font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.TITLE') }}
            </h3>
            <p class="text-n-slate-11 text-sm leading-6">
              {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="flex gap-2">
          <Button
            v-if="isConnected && studioDashboardUrl"
            :label="$t('INTEGRATION_SETTINGS.AGENT_STUDIO.OPEN_STUDIO')"
            icon="i-ph-arrow-square-out"
            blue
            @click="openStudio"
          />
          <Button
            :label="syncButtonLabel"
            :disabled="isSyncing || !settings.configured"
            :is-loading="isSyncing"
            faded
            slate
            @click="handleSync"
          />
        </div>
      </div>

      <!-- Not Available Warning -->
      <div
        v-if="!settings.configured"
        class="flex items-center gap-4 p-4 rounded-md bg-n-amber-3 border border-n-amber-6"
      >
        <span class="i-ph-warning text-2xl text-n-amber-11" />
        <div>
          <h4 class="font-medium text-n-amber-12">
            {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.NOT_AVAILABLE.TITLE') }}
          </h4>
          <p class="text-sm text-n-amber-11">
            {{
              $t('INTEGRATION_SETTINGS.AGENT_STUDIO.NOT_AVAILABLE.DESCRIPTION')
            }}
          </p>
        </div>
      </div>

      <!-- Status Card -->
      <div
        v-if="settings.configured"
        class="p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <h4 class="mb-4 text-lg font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.STATUS.TITLE') }}
        </h4>

        <div class="grid gap-4">
          <!-- Connection Status -->
          <div class="flex items-center gap-3">
            <span
              class="w-3 h-3 rounded-full"
              :class="isConnected ? 'bg-n-teal-9' : 'bg-n-ruby-9'"
            />
            <span class="text-n-slate-11">
              {{
                isConnected
                  ? $t('INTEGRATION_SETTINGS.AGENT_STUDIO.STATUS.CONNECTED')
                  : $t('INTEGRATION_SETTINGS.AGENT_STUDIO.STATUS.NOT_CONNECTED')
              }}
            </span>
          </div>

          <!-- Organization ID -->
          <div
            class="flex items-center justify-between py-3 border-t border-n-weak"
          >
            <span class="text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.ORGANIZATION_ID') }}
            </span>
            <span class="font-mono text-sm text-n-slate-12">
              {{ settings.organization_id || '-' }}
            </span>
          </div>

          <!-- User Synced -->
          <div
            class="flex items-center justify-between py-3 border-t border-n-weak"
          >
            <span class="text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USER_SYNCED') }}
            </span>
            <span
              class="px-2 py-1 text-xs font-medium rounded"
              :class="
                settings.user_synced
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{
                settings.user_synced
                  ? $t('INTEGRATION_SETTINGS.AGENT_STUDIO.YES')
                  : $t('INTEGRATION_SETTINGS.AGENT_STUDIO.NO')
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Users List -->
      <div
        v-if="settings.configured && settings.users.length > 0"
        class="p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <h4 class="mb-4 text-lg font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USERS.TITLE') }}
        </h4>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak">
                <th class="text-left py-3 px-2 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USERS.NAME') }}
                </th>
                <th class="text-left py-3 px-2 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USERS.EMAIL') }}
                </th>
                <th class="text-left py-3 px-2 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USERS.ROLE') }}
                </th>
                <th class="text-center py-3 px-2 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USERS.CONNECTED') }}
                </th>
                <th class="text-center py-3 px-2 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.AGENT_STUDIO.USERS.MEMBER') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in settings.users"
                :key="user.id"
                class="border-b border-n-weak last:border-0"
              >
                <td class="py-3 px-2 text-n-slate-12">
                  {{ user.name }}
                </td>
                <td class="py-3 px-2 text-n-slate-11">
                  {{ user.email }}
                </td>
                <td class="py-3 px-2">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded capitalize"
                    :class="getRoleBadgeClass(user.role)"
                  >
                    {{ user.role }}
                  </span>
                </td>
                <td class="py-3 px-2 text-center">
                  <span
                    v-if="user.connected"
                    class="i-ph-check-circle-fill text-lg text-n-teal-9"
                  />
                  <span v-else class="i-ph-x-circle text-lg text-n-slate-8" />
                </td>
                <td class="py-3 px-2 text-center">
                  <span
                    v-if="user.is_member"
                    class="i-ph-check-circle-fill text-lg text-n-teal-9"
                  />
                  <span v-else class="i-ph-x-circle text-lg text-n-slate-8" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        class="flex items-center gap-4 p-4 rounded-md bg-n-ruby-3 border border-n-ruby-6"
      >
        <span class="i-ph-warning-circle text-2xl text-n-ruby-11" />
        <p class="text-sm text-n-ruby-11">
          {{ error }}
        </p>
      </div>
    </div>
  </div>
</template>
